import {ElementRef, Renderer2} from "@angular/core";

export type Placement = "left" | "top" | "right" | "bottom";
export type HorizontalAlign = "center" | "left" | "right";
/**
 * Padding from window frame
 */
const PADDING = 20;

export interface IRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface IPosition {
  left: number;
  top: number;

  arrowPos: IArrowPosition;
}

export interface IBox {
  width: number;
  height: number;
}

export interface IArrowPosition {
  marginLeft?: string;
  marginTop?: string;
  left?: string;
  top?: string;
  right?: string;
  bottom?: string;

  arrowPlacement?: Placement;
}

interface ICalcResult {
  arrowPos: IArrowPosition;
  pos: number;
}

interface IPoint {
  x: number;
  y: number;
}

interface ICalcProps {
  centerPoint: IPoint;
  box: IBox;
  container: IBox;
  target: IRect;
  arrowSize: IBox;
  paddings: IRect;
}

/**
 * Calculate position of the @box relative to @target inside @container includes @paddings
 * Also calculate arrow position and styles
 */
export function calculatePosition(placement: Placement,
                                  target: IRect,
                                  box: IBox,
                                  container: IBox,
                                  paddings: IRect = {top: PADDING, bottom: PADDING, right: PADDING, left: PADDING},
                                  arrowSize: IBox = {width: 10, height: 10},
                                  horizontalAlign: HorizontalAlign = "center"): IPosition {

  let pointX: number;
  let pointY: number;
  let arrowPos: IArrowPosition;
  const calcProps: ICalcProps = {centerPoint: null, box, container, target, arrowSize, paddings};
  const targetWidth = target.right - target.left;
  // const rectHeight = near.bottom - near.top;
  //
  switch (placement) {
    case "left":
      calcProps.centerPoint = calcCenterLeft(target);

      let leftResult = calcLeft(calcProps);
      if (!leftResult) {
        calcProps.centerPoint = calcCenterRight(target);
        leftResult = calcRight(calcProps);
      }
      if (!leftResult) {
        leftResult = calcMaxLeft(calcProps);
      }

      pointX = leftResult.pos;
      arrowPos = leftResult.arrowPos;

      pointY = calcVerticalCenter(
        calcProps.centerPoint.y,
        box.height,
        container.height,
        paddings.bottom,
        paddings.top,
        arrowSize.height,
        arrowPos
      );

      break;
    case "top":
      calcProps.centerPoint = calcCenterTop(target);

      let topResult = calcTop(calcProps);
      if (!topResult) {
        calcProps.centerPoint = calcCenterBottom(target);
        topResult = calcBottom(calcProps);
      }
      if (!topResult) {
        topResult = calcMaxTop(calcProps);
      }

      pointY = topResult.pos;
      arrowPos = topResult.arrowPos;

      pointX = calcHorizontalCenter(
        calcProps.centerPoint.x,
        targetWidth,
        box.width,
        paddings.left,
        paddings.right,
        container.width,
        arrowSize.width,
        arrowPos,
        horizontalAlign
      );
      break;
    case "right":
      calcProps.centerPoint = calcCenterRight(target);

      let rightResult = calcRight(calcProps);
      if (!rightResult) {
        calcProps.centerPoint = calcCenterLeft(target);
        rightResult = calcLeft(calcProps);
      }
      if (!rightResult) {
        rightResult = calcMaxRight(calcProps);
      }

      pointX = rightResult.pos;
      arrowPos = rightResult.arrowPos;

      pointY = calcVerticalCenter(
        calcProps.centerPoint.y,
        box.height,
        container.height,
        paddings.bottom,
        paddings.top,
        arrowSize.height,
        arrowPos
      );
      break;
    case "bottom":
      calcProps.centerPoint = calcCenterBottom(target);

      let bottomResult = calcBottom(calcProps);
      if (!bottomResult) {
        calcProps.centerPoint = calcCenterTop(target);
        bottomResult = calcTop(calcProps);
      }
      if (!bottomResult) {
        bottomResult = calcMaxBottom(calcProps);
      }

      pointY = bottomResult.pos;
      arrowPos = bottomResult.arrowPos;

      pointX = calcHorizontalCenter(
        calcProps.centerPoint.x,
        targetWidth,
        box.width,
        paddings.left,
        paddings.right,
        container.width,
        arrowSize.width,
        arrowPos,
        horizontalAlign
      );
      break;

  }

  return {
    left: pointX,
    top: pointY,
    arrowPos
  };

}

export function applyArrowStyles(renderer: Renderer2, arrow: ElementRef, arrowPos: IArrowPosition) {

  if (arrowPos.left) {
    renderer.setStyle(arrow.nativeElement, "left", arrowPos.left);
  }

  if (arrowPos.right) {
    renderer.setStyle(arrow.nativeElement, "right", arrowPos.right);
  }

  if (arrowPos.top) {
    renderer.setStyle(arrow.nativeElement, "top", arrowPos.top);
  }

  if (arrowPos.bottom) {
    renderer.setStyle(arrow.nativeElement, "bottom", arrowPos.bottom);
  }

  if (arrowPos.marginTop) {
    renderer.setStyle(arrow.nativeElement, "margin-top", arrowPos.marginTop);
  }

  if (arrowPos.marginLeft) {
    renderer.setStyle(arrow.nativeElement, "margin-left", arrowPos.marginLeft);
  }

}

function calcCenterLeft(target: IRect) {
  return {
    x: target.left,
    y: (target.bottom - target.top) / 2 + target.top
  };
}

function calcCenterRight(target: IRect) {
  return {
    x: target.right,
    y: (target.bottom - target.top) / 2 + target.top
  };
}

function calcCenterTop(target: IRect) {
  return {
    x: (target.right - target.left) / 2 + target.left,
    y: target.top,
  };
}

function calcCenterBottom(target: IRect) {
  return {
    x: (target.right - target.left) / 2 + target.left,
    y: target.bottom,
  };
}

function calcLeft(props: ICalcProps): ICalcResult {
  const arrowPos: IArrowPosition = {};

  if (props.centerPoint.x - props.box.width >= props.paddings.left) {
    // open at the left side
    arrowPos.right = -props.arrowSize.width + "px";
    arrowPos.arrowPlacement = "right";
    return {arrowPos, pos: (props.centerPoint.x - props.box.width - props.arrowSize.width)};
  }

  return null;
}

function calcMaxLeft(props: ICalcProps): ICalcResult {
  return {
    pos: props.paddings.left,
    arrowPos: {
      right: -props.arrowSize.width + "px",
      arrowPlacement: "right",
    },
  };
}


function calcRight(props: ICalcProps): ICalcResult {
  const arrowPos: IArrowPosition = {};

  if (props.centerPoint.x + props.box.width + props.paddings.right <= props.container.width) {
    // open at the right side
    arrowPos.left = -props.arrowSize.width + "px";
    arrowPos.arrowPlacement = "left";
    return {pos: props.centerPoint.x + props.arrowSize.width, arrowPos};
  }

  return null;
}

function calcMaxRight(props: ICalcProps): ICalcResult {
  return {
    pos: props.container.width - props.paddings.right - props.box.width,
    arrowPos: {
      left: -props.arrowSize.width + "px",
      arrowPlacement: "left",
    },
  };
}


function calcBottom(props: ICalcProps): ICalcResult {
  const arrowPos: IArrowPosition = {};

  if (props.centerPoint.y + props.box.height + props.paddings.bottom <= props.container.height) {
    // open at the bottom
    arrowPos.top = -props.arrowSize.height + "px";
    arrowPos.arrowPlacement = "top";
    return {
      pos: props.centerPoint.y + props.arrowSize.height,
      arrowPos
    };
  }

  return null;
}

function calcMaxBottom(props: ICalcProps): ICalcResult {
  return {
    pos: props.container.height - props.paddings.bottom - props.box.height,
    arrowPos: {
      top: -props.arrowSize.height + "px",
      arrowPlacement: "top",
    },
  };
}


function calcTop(props: ICalcProps): ICalcResult {
  const arrowPos: IArrowPosition = {};

  if (props.centerPoint.y - props.box.height >= props.paddings.top) {
    // open at the top:
    arrowPos.bottom = -props.arrowSize.height + "px";
    arrowPos.arrowPlacement = "bottom";
    return {
      arrowPos,
      pos: props.centerPoint.y - props.box.height - props.arrowSize.height
    };
  }

  return null;
}

function calcMaxTop(props: ICalcProps): ICalcResult {
  return {
    pos: props.paddings.top,
    arrowPos: {
      top: -props.arrowSize.height + "px",
      arrowPlacement: "top",
    },
  };
}


function calcHorizontalCenter(placementX: number,
                              targetWidth: number,
                              boxWidth: number,
                              paddingLeft: number,
                              paddingRight: number,
                              containerWidth: number,
                              arrowWidth: number,
                              arrowPos: IArrowPosition,
                              horizontalAlign: HorizontalAlign): number {
  switch (horizontalAlign) {
    case "left":
      placementX = placementX + (boxWidth / 2) - (targetWidth / 2);
      break;
    case "right":
      placementX = placementX + (targetWidth / 2) - (boxWidth / 2);
      break;
  }
  if (placementX - boxWidth / 2 >= paddingLeft &&
    placementX + boxWidth / 2 + paddingRight <= containerWidth) {
    switch (horizontalAlign) {
      case "left":
        arrowPos.left = (targetWidth / 2) + "px";
        arrowPos.marginLeft = -(arrowWidth / 2) + "px";
        break;
      case "right":
        arrowPos.left = boxWidth - (targetWidth / 2) + "px";
        arrowPos.marginLeft = -(arrowWidth / 2) + "px";
        break;
      default:
        arrowPos.left = "50%";
        arrowPos.marginLeft = -(arrowWidth / 2) + "px";
        break;
    }
    // at the center of the box
    return (placementX - boxWidth / 2);
  } else {
    let leftPos = placementX - boxWidth / 2;
    if (leftPos < paddingLeft) {
      // on the left side:
      switch (horizontalAlign) {
        case "left":
          arrowPos.left = (targetWidth / 2) + "px";
          arrowPos.marginLeft = -(arrowWidth / 2) + "px";
          break;
        case "right":
          arrowPos.left = placementX + paddingLeft + (arrowWidth / 2) + "px";
          break;
        default:
          arrowPos.left = "50%";
          arrowPos.marginLeft = -((paddingLeft - leftPos) + (arrowWidth / 2)) + "px";
          break;
      }
      return paddingLeft;
    } else {
      // on the right side
      switch (horizontalAlign) {
        case "left":
          const offset = containerWidth - paddingRight - boxWidth;
          // честно говоря я сам не до конца понял как это работает. =>
          arrowPos.left = placementX - offset - targetWidth - (targetWidth / 2) + "px";
          break;
        case "right":
          arrowPos.left = boxWidth - (targetWidth / 2) + "px";
          arrowPos.marginLeft = -(arrowWidth / 2) + "px";
          break;
        default:
          // containerWidth - paddingRight - boxWidth; // => будет работать и так. не знаю зачем тут placementX прикручен... =>
          const rightOffset = containerWidth - paddingRight - placementX;
          arrowPos.left = boxWidth - rightOffset - (arrowWidth / 2) + "px";
          break;
      }
      leftPos = containerWidth - boxWidth - paddingRight;
      return leftPos;
    }
  }
}

function calcVerticalCenter(placementY: number,
                            boxHeight: number,
                            containerHeight: number,
                            paddingBottom: number,
                            paddingTop: number,
                            arrowHeight: number,
                            arrowPos: IArrowPosition): number {

  if (placementY - boxHeight / 2 >= paddingTop && placementY + boxHeight / 2 + paddingBottom <= containerHeight) {
    arrowPos.top = "50%";
    arrowPos.marginTop = -(arrowHeight / 2) + "px";
    return (placementY - boxHeight / 2);

  } else if (placementY - boxHeight / 2 < paddingTop) {
    // stick to top side
    const topStyle = placementY - paddingTop;
    arrowPos.top = (topStyle > 0 ? topStyle : 4) + "px";
    return paddingTop;
  } else {
    // stick to bottom
    const top = containerHeight - boxHeight - (paddingBottom / 2);
    const topStyle = (placementY - top);
    arrowPos.top = (topStyle > 0 ? topStyle : 4) + "px";
    return top;
  }
}


